// DetailCard — the shared floating detail drawer behind both My Access detail
// views. A grant (AccessDetailDrawer) and an awaiting-approval request
// (RequestDetailDrawer) render the identical chrome — header, a Status section,
// a History section (prior requests + team adoption), an Activity timeline, and
// a footer action — differing ONLY in the status slot and the footer action.
//
// Floating-card chrome (even margin, blurred backdrop, rounded 20px) mirrors
// components/tasks/TaskDrawer.tsx. Pure time/status/timeline logic lives in
// ./detailLogic (kept React-free so it stays unit-testable).

import {
  Box,
  Drawer,
  DrawerBody,
  DrawerContent,
  DrawerOverlay,
  Flex,
  HStack,
  Icon,
  Text,
} from "@chakra-ui/react";
import { ReactNode } from "react";
import { TbX } from "react-icons/tb";

import AppLogo from "components/ds/AppLogo";
import BreadcrumbRow from "components/ds/BreadcrumbRow";
import Chip, { type ChipTone } from "components/ds/Chip";
import SectionHeader from "components/ds/SectionHeader";
import PriorRequests from "components/tasks/PriorRequests";
import ResourceDetailCard, {
  ResourceRow,
} from "components/tasks/ResourceDetailCard";
import TeamAdoption from "components/tasks/TeamAdoption";
import { humanDt } from "modules/utils";

import {
  type DetailCardHistory,
  hasHistory,
  type TimelineEvent,
} from "./detailLogic";

// Re-export the pure helpers the sibling drawers pull from "./DetailCard".
export {
  fmtDate,
  fullName,
  sentLabel,
  type DetailCardHistory,
  type TimelineEvent,
} from "./detailLogic";

// Compact label/value line used inside the status cards.
export const MetaRow = ({
  label,
  children,
}: {
  label: string;
  children: ReactNode;
}) => (
  <Flex justify="space-between" align="baseline" gap="md" fontSize="smd">
    <Box as="span" color="neutral.700" flexShrink={0}>
      {label}
    </Box>
    <Box as="span" color="neutral.950" fontWeight={500} textAlign="right">
      {children}
    </Box>
  </Flex>
);

// One stat tile in the History header row.
const Stat = ({
  label,
  value,
  color,
}: {
  label: string;
  value: number;
  color: string;
}) => (
  <Box
    flex="1"
    bg="neutral.0"
    borderWidth="1px"
    borderColor="neutral.200"
    borderRadius="10px"
    px="md"
    py="sm"
  >
    <Text
      fontSize="2xl"
      fontWeight={600}
      color={color}
      lineHeight="1.1"
      sx={{ fontVariantNumeric: "tabular-nums" }}
    >
      {value}
    </Text>
    <Text fontSize="xs" color="neutral.600" mt="2px">
      {label}
    </Text>
  </Box>
);

const ActivityTimeline = ({ events }: { events: TimelineEvent[] }) => (
  <Box position="relative" pl="2px">
    {events.map((e, i) => {
      const last = i === events.length - 1;
      return (
        <Flex key={e.id} gap="md" position="relative" pb={last ? 0 : "lg"}>
          {!last && (
            <Box
              position="absolute"
              left="13px"
              top="26px"
              bottom="-2px"
              w="2px"
              bg="neutral.200"
            />
          )}
          <Flex
            align="center"
            justify="center"
            flexShrink={0}
            w="28px"
            h="28px"
            borderRadius="full"
            bg="neutral.0"
            borderWidth="1px"
            borderColor="neutral.200"
            color={e.color}
            zIndex={1}
          >
            <Icon as={e.icon} boxSize="15px" />
          </Flex>
          <Box pt="2px" minW={0}>
            <Text
              fontSize="smd"
              fontWeight={600}
              color="neutral.950"
              lineHeight="1.3"
            >
              {e.label}
            </Text>
            {e.detail && (
              <Text fontSize="smd" color="neutral.700" mt="1px">
                {e.detail}
              </Text>
            )}
            <Text
              fontSize="xs"
              color="neutral.500"
              mt="2px"
              sx={{ fontVariantNumeric: "tabular-nums" }}
            >
              {humanDt(new Date(e.at))}
            </Text>
          </Box>
        </Flex>
      );
    })}
  </Box>
);

// History section — stat tiles + the history-focused resource card. Rendered
// only when the server actually supplied insight data (prior requests or team
// adoption); otherwise it's omitted rather than showing empty scaffolding.
const HistorySection = ({
  history,
  name,
  logoUrl,
}: {
  history: DetailCardHistory;
  name: string;
  logoUrl?: string;
}) => {
  const prior = history.priorRequests;
  const approved = prior?.approved ?? 0;
  const denied = prior?.denied ?? 0;

  return (
    <Box>
      <SectionHeader>History</SectionHeader>
      <Flex gap="sm" mb="md">
        <Stat
          label="Times requested"
          value={approved + denied}
          color="neutral.950"
        />
        <Stat label="Approved" value={approved} color="success.600" />
        <Stat label="Denied" value={denied} color="error.600" />
      </Flex>
      <ResourceDetailCard
        name={name}
        imageUrl={logoUrl}
        catalogItemName={history.catalogItemName}
        catalogName={history.catalogName}
      >
        <ResourceRow label="Decision history" alignTop>
          <PriorRequests data={prior} />
        </ResourceRow>
        {history.teamAdoption && (
          <ResourceRow label="Team adoption" alignTop>
            <TeamAdoption data={history.teamAdoption} />
          </ResourceRow>
        )}
      </ResourceDetailCard>
    </Box>
  );
};

export interface DetailCardHeader {
  itemName: string;
  /** Breadcrumb segments ("catalog › app"). */
  crumbs: string[];
  logoUrl?: string;
  statusChip: { tone: ChipTone; label: string };
  /** Shown beside the status chip (the passkey "Signed" badge). */
  badge?: ReactNode;
}

export interface DetailCardProps {
  isOpen: boolean;
  onClose: () => void;
  /**
   * Chakra returns focus to whatever opened the drawer as it closes. Pass false
   * when the drawer is only stepping aside for a dialog it just opened, so it
   * doesn't yank focus out of that dialog on the way out. Default true.
   */
  returnFocusOnClose?: boolean;
  header: DetailCardHeader;
  /** Section label above the status slot ("Status" | "Approval status"). */
  statusLabel: string;
  statusSlot: ReactNode;
  history: DetailCardHistory;
  activity: TimelineEvent[];
  footer: ReactNode;
}

const DetailCard = ({
  isOpen,
  onClose,
  returnFocusOnClose = true,
  header,
  statusLabel,
  statusSlot,
  history,
  activity,
  footer,
}: DetailCardProps) => (
  <Drawer
    isOpen={isOpen}
    onClose={onClose}
    placement="right"
    autoFocus={false}
    returnFocusOnClose={returnFocusOnClose}
  >
    <DrawerOverlay
      bg="linear-gradient(90deg, rgba(0,0,0,0.08) 0%, rgba(0,0,0,0.14) 100%)"
      sx={{
        backdropFilter: "blur(8px) saturate(120%)",
        WebkitBackdropFilter: "blur(8px) saturate(120%)",
      }}
    />
    <DrawerContent
      w="34vw"
      minW="420px"
      maxW="560px"
      // Even margin detaches the panel from the viewport edges so it reads as a
      // floating card rather than a flush slide-over (Chakra positions via the
      // flex container, so margin — not insets — moves it inward).
      m="16px"
      h="auto"
      maxH="calc(100vh - 32px)"
      bg="neutral.0"
      borderRadius="20px"
      borderWidth="1px"
      borderColor="neutral.200"
      boxShadow="0 24px 56px -16px rgba(16,24,40,0.22), 0 8px 20px -10px rgba(16,24,40,0.12)"
      overflow="hidden"
      display="flex"
      flexDirection="column"
    >
      {/* header */}
      <Flex
        align="flex-start"
        gap="md"
        px="20px"
        pt="20px"
        pb="md"
        borderBottomWidth="1px"
        borderColor="neutral.200"
      >
        <AppLogo name={header.itemName} logoUrl={header.logoUrl} size="lg" />
        <Box flex="1" minW={0}>
          {header.crumbs.length > 0 && (
            <BreadcrumbRow segments={header.crumbs} />
          )}
          <Text
            fontSize="lg"
            fontWeight={600}
            color="neutral.950"
            lineHeight="1.3"
            sx={{ textWrap: "balance" }}
          >
            {header.itemName}
          </Text>
          <HStack
            spacing="sm"
            mt="1xs"
            flexWrap="wrap"
            fontSize="md"
            color="neutral.700"
          >
            <Box as="span">Status:</Box>
            <Chip tone={header.statusChip.tone} dot={header.statusChip.tone}>
              {header.statusChip.label}
            </Chip>
            {header.badge}
          </HStack>
        </Box>
        <Box
          as="button"
          type="button"
          onClick={onClose}
          aria-label="Close"
          w="32px"
          h="32px"
          borderRadius="component.lg"
          display="inline-flex"
          alignItems="center"
          justifyContent="center"
          color="neutral.700"
          flexShrink={0}
          transition="background-color 120ms ease-out, transform 120ms ease-out"
          _hover={{ bg: "neutral.100" }}
          _active={{ transform: "scale(0.96)" }}
        >
          <Icon as={TbX} boxSize="18px" />
        </Box>
      </Flex>

      {/* body — Status → History → Activity */}
      <DrawerBody
        px="20px"
        py="20px"
        display="flex"
        flexDirection="column"
        gap="20px"
      >
        <Box>
          <SectionHeader>{statusLabel}</SectionHeader>
          {statusSlot}
        </Box>

        {hasHistory(history) && (
          <HistorySection
            history={history}
            name={header.itemName}
            logoUrl={header.logoUrl}
          />
        )}

        {activity.length > 0 && (
          <Box>
            <SectionHeader>Activity</SectionHeader>
            <ActivityTimeline events={activity} />
          </Box>
        )}
      </DrawerBody>

      {/* footer */}
      <Flex
        align="center"
        gap="sm"
        px="20px"
        py="lg"
        borderTopWidth="1px"
        borderColor="neutral.200"
        bg="neutral.25"
      >
        {footer}
      </Flex>
    </DrawerContent>
  </Drawer>
);

export default DetailCard;
